// Tenantra Inc. - Tenant Consent Page (e-signed via Dropbox Sign)
import { useState, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import api from '@/services/api';
import { track } from '@/services/analytics';

const SIGNWELL_EMBED_SRC = 'https://static.signwell.com/assets/embedded.js';

function loadSignWell() {
  if (window.SignWellEmbed) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SIGNWELL_EMBED_SRC;
    s.onload = resolve;
    s.onerror = () => reject(new Error('embed load failed'));
    document.head.appendChild(s);
  });
}

export default function ConsentPage() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [approved, setApproved] = useState(false);
  const [signStatus, setSignStatus] = useState(null);
  const signClient = useRef(null);

  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const tenancyId = urlParams.get('tenancy');

  useEffect(() => {
    if (token && tenancyId) {
      fetchConsentDetails();
    } else {
      setError('Invalid consent link');
      setLoading(false);
    }
    return () => { try { signClient.current?.close(); } catch (e) { /* noop */ } };
  }, []);

  const fetchConsentDetails = async () => {
    try {
      const response = await api.get(`/tenancies/consent/details?token=${token}&tenancy_id=${tenancyId}`);
      setDetails(response.data);
      if (response.data.already_approved) setApproved(true);
    } catch {
      setError('This consent link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  };

  const completeSigning = async () => {
    for (let attempt = 0; attempt < 5; attempt++) {
      try {
        const response = await api.post('/esign/consent/complete', { token, tenancy_id: tenancyId });
        setDetails(prev => ({ ...prev, tenant_unique_id: response.data.tenant_unique_id }));
        setApproved(true);
        track('consent_signed', { tenancy_id: tenancyId });
        toast.success('Consent signed');
        return;
      } catch (err) {
        if (err.response?.status !== 409) break;
        await new Promise(r => setTimeout(r, 1500));
      }
    }
    setSignStatus('pending_confirmation');
    setSubmitting(false);
  };

  const handleSign = async () => {
    if (!consentChecked) {
      toast.error('Please confirm you have read the disclosure');
      return;
    }
    try {
      setSubmitting(true);
      const response = await api.post('/esign/consent/start', { token, tenancy_id: tenancyId });
      if (response.data.status === 'signed') {
        await completeSigning();
        return;
      }
      await loadSignWell();
      let signed = false;
      const embed = new window.SignWellEmbed({
        url: response.data.sign_url,
        events: {
          completed: () => { signed = true; setSignStatus('signed'); completeSigning(); setTimeout(() => embed.close(), 1500); },
          declined: () => { toast.error('Consent was declined'); setSubmitting(false); },
          closed: () => { if (!signed) setSubmitting(false); },
          error: () => { toast.error('The signing window could not be loaded'); setSubmitting(false); },
        },
      });
      signClient.current = embed;
      embed.open();
      track('consent_sign_opened', { tenancy_id: tenancyId });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start signing. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="max-w-sm w-full text-center">
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <span className="text-red-500 text-sm font-bold">!</span>
          </div>
          <p className="text-sm font-medium text-slate-900 mb-1">Invalid Link</p>
          <p className="text-xs text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  if (approved) {
    return (
      <div className="min-h-screen bg-white" data-testid="consent-approved">
        <Header />
        <div className="max-w-md mx-auto px-6 py-10 text-center">
          <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <span className="text-emerald-600 text-sm font-bold">&#10003;</span>
          </div>
          <p className="text-sm font-medium text-slate-900 mb-1">Consent Confirmed</p>
          <p className="text-xs text-slate-500 mb-6">
            Your tenancy at {details?.property_address} has been approved.
          </p>
          {details?.tenant_unique_id && (
            <div className="border border-slate-200 rounded-md p-4 mb-6 text-left">
              <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-1">Your Tenant ID</p>
              <p className="text-base font-semibold text-slate-900 font-mono tracking-wide">{details.tenant_unique_id}</p>
              <p className="text-[10px] text-slate-400 mt-1.5">Provide this to future landlords to link your rental history.</p>
            </div>
          )}
          <a href="/login">
            <Button className="bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium w-full h-10" data-testid="consent-sign-in-btn">
              Sign In to Tenantra
            </Button>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="consent-page">
      <Header />

      <div className="max-w-md mx-auto px-6 py-6">
        {/* Title */}
        <div className="mb-6">
          <h1 className="text-sm font-semibold text-slate-900 mb-0.5" data-testid="consent-page-title">
            Tenancy Consent Request
          </h1>
          <p className="text-xs text-slate-400">Review the details below and provide your authorization.</p>
        </div>

        {/* Tenancy Details */}
        <div className="mb-6" data-testid="tenancy-details-card">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-3">Tenancy Details</p>
          <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
            <Row label="Property" value={details?.property_address} />
            <Row label="Landlord" value={details?.landlord_name} />
            <Row label="Move-in Date" value={new Date(details?.move_in_date).toLocaleDateString()} />
            <Row label="Rent" value={`$${details?.rent_amount?.toFixed(2)} / ${details?.rent_frequency}`} />
          </div>
        </div>

        {/* Disclosure */}
        <div className="mb-6" data-testid="disclosure-card">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-3">Disclosure & Consent</p>
          <div className="border border-slate-200 rounded-md p-4">
            <p className="text-xs text-slate-600 mb-3 leading-relaxed">
              By approving this tenancy, you consent to the following:
            </p>
            <ol className="text-xs text-slate-600 leading-relaxed space-y-2 mb-4 list-none">
              <li className="flex gap-2"><span className="text-slate-300 font-medium">1.</span><span>Your landlord may report your rental payment behaviour to Tenantra for the duration of your tenancy.</span></li>
              <li className="flex gap-2"><span className="text-slate-300 font-medium">2.</span><span>This includes rent amounts, payment dates, on-time, late, partial or missed payments, and lease compliance.</span></li>
              <li className="flex gap-2"><span className="text-slate-300 font-medium">3.</span><span>Tenantra may furnish this history to Canadian credit reporting agencies, including Equifax Canada.</span></li>
              <li className="flex gap-2"><span className="text-slate-300 font-medium">4.</span><span>Ongoing reporting for the full duration of your lease is recommended. Both positive and negative behaviour is reported.</span></li>
              <li className="flex gap-2"><span className="text-slate-300 font-medium">5.</span><span>Your Rent Report is accessible to you at any time, and you may dispute any entry you believe is inaccurate.</span></li>
              <li className="flex gap-2"><span className="text-slate-300 font-medium">6.</span><span>Your data is protected under PIPEDA. You may withdraw consent in writing.</span></li>
            </ol>
            <p className="text-[10px] text-slate-400 leading-relaxed">
              The full consent document will open for your electronic signature. A signed copy is retained for audit purposes.
            </p>
          </div>
        </div>

        {/* Consent Checkbox */}
        <label htmlFor="consent" className="flex items-start gap-3 mb-6 cursor-pointer border border-slate-200 rounded-md p-4" data-testid="consent-checkbox-section">
          <Checkbox
            id="consent"
            checked={consentChecked}
            onCheckedChange={setConsentChecked}
            className="mt-px flex-shrink-0"
            data-testid="consent-checkbox"
          />
          <span className="text-xs text-slate-600 leading-relaxed">
            I have read and understand the above disclosure and I am ready to sign the consent document.
          </span>
        </label>

        {signStatus === 'pending_confirmation' && (
          <p className="text-xs text-amber-600 mb-4" data-testid="consent-pending-confirmation">
            Your signature was received and is being confirmed. You can close this page; your landlord will see the signed consent shortly.
          </p>
        )}

        {/* Buttons */}
        <div className="flex gap-3 mb-8" data-testid="consent-actions">
          <Button
            variant="outline"
            className="flex-1 h-10 text-xs font-medium text-slate-500 border-slate-200"
            onClick={() => window.close()}
            data-testid="consent-decline-btn"
          >
            Decline
          </Button>
          <Button
            onClick={handleSign}
            disabled={!consentChecked || submitting}
            className="flex-1 h-10 text-xs font-medium bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="consent-sign-btn"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Review & Sign'}
          </Button>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 pt-4 pb-6">
          <p className="text-[10px] text-slate-300 text-center leading-relaxed">
            Tenantra Inc. &middot; 100 King Street West, Suite 5600, Toronto, ON &middot; PIPEDA Compliant
          </p>
        </div>
      </div>
    </div>
  );
}

function Header() {
  return (
    <div className="border-b border-slate-100 px-6 py-3 flex items-center justify-between max-w-md mx-auto">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 bg-teal-600 rounded flex items-center justify-center">
          <span className="text-white text-[10px] font-bold">T</span>
        </div>
        <span className="text-xs font-semibold text-slate-900 tracking-tight">TENANTRA</span>
      </div>
      <span className="text-[10px] text-slate-300">Consent Form</span>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="px-4 py-3">
      <p className="text-[10px] text-slate-400 mb-0.5">{label}</p>
      <p className="text-xs font-medium text-slate-800">{value}</p>
    </div>
  );
}
